import { useEffect, useMemo, useState } from 'react';
import { Car, ChevronLeft, ChevronRight, ParkingSquare, UsersRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/Shared/Card';
import { Button } from '@/components/Shared/Button';
import { Badge } from '@/components/Shared/Badge';
import { ChoiceTiles } from '@/components/Shared/ChoiceTiles';
import { EmptyState } from '@/components/Shared/EmptyState';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/Shared/Table';
import { ParkVehicleDialog } from '@/components/Dashboard/Employee/ParkVehicleDialog';
import { useMyVehicleTripsStore, type MyTrip, type UnparkedTrip } from '@/store/useMyVehicleTripsStore';
import { formatDate, isoDate, parseUtcTimestamp } from '@/utils/dateUtils';
import { formatKm } from '@/utils/distance';
import { DRIVER, drivenKm, filterTripsByRole, isTripRunning, shownDistanceKm, type TripRoleFilter } from '@/utils/vehicleTrips';
import { describeTripPeople, describeTripPurpose, describeTripRoute, describeTripSpan } from './tripText';

/** First and last local day of the month `offset` months from now. */
function monthPeriod(offset: number): { from: string; to: string; label: Date } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  return { from: isoDate(first), to: isoDate(last), label: first };
}

/** "Fahrer" (FF green, car) or "Beifahrer" (slate) — the first thing every row says. */
function RoleBadge({ trip }: { trip: MyTrip }) {
  const { t } = useTranslation('timeclock');
  const driver = trip.role === DRIVER;
  const Icon = driver ? Car : UsersRound;
  return (
    <Badge color={driver ? 'green' : 'gray'} className="gap-1">
      <Icon className="w-3 h-3" strokeWidth={2} aria-hidden />
      {t(driver ? 'trips.role.driver' : 'trips.role.passenger')}
    </Badge>
  );
}

/**
 * Tab "Fahrten" of the time clock (FAFD-68): the employee's own trips (driven) and rides
 * (travelled with), month by month — never other people's trips; the logbook stays with the
 * administration. Each row starts with the role; kilometres, route and purpose belong to own
 * trips only, a ride names its driver. A trip left without a reading can be parked right here.
 */
export function MyTripsTab() {
  const { t } = useTranslation(['timeclock', 'vehicles']);
  const { trips, tripsLoading, tripsError, unparked, fetchTrips, fetchMyUnparked } = useMyVehicleTripsStore();
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<TripRoleFilter>('all');
  const [parking, setParking] = useState<UnparkedTrip | null>(null);
  const period = useMemo(() => monthPeriod(offset), [offset]);

  useEffect(() => {
    void fetchTrips(period.from, period.to);
    void fetchMyUnparked();
  }, [period, fetchTrips, fetchMyUnparked]);

  const sorted = useMemo(
    () => filterTripsByRole(trips, filter).sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? '')),
    [trips, filter],
  );
  const unparkedOf = (trip: MyTrip) => unparked.find((u) => u.entryId === trip.id) ?? null;

  return (
    <Card
      title={t('trips.title')}
      actions={
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setOffset((o) => o - 1)} aria-label={t('trips.previousMonth')} className="p-1.5">
            <ChevronLeft className="w-4 h-4" strokeWidth={2} />
          </Button>
          <span className="min-w-[110px] text-center text-[13px] font-medium text-slate-900 tabular-nums">
            {formatDate(period.label, { month: 'long', year: 'numeric' })}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setOffset((o) => Math.min(0, o + 1))} disabled={offset === 0} aria-label={t('trips.nextMonth')} className="p-1.5">
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </Button>
        </div>
      }
    >
      {tripsError ? (
        <EmptyState variant="error" title={t('trips.loadError')} description={tripsError} onRetry={() => fetchTrips(period.from, period.to)} />
      ) : !tripsLoading && trips.length === 0 ? (
        <EmptyState icon={<Car className="w-12 h-12" strokeWidth={1.75} />} title={t('trips.empty')} description={t('trips.emptyHint')} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div className="w-full sm:max-w-md">
              <ChoiceTiles
                legend={t('trips.filter.label')}
                compact
                value={filter}
                onChange={setFilter}
                options={[
                  { value: 'all', label: t('trips.filter.all') },
                  { value: 'driver', label: t('trips.filter.driver') },
                  { value: 'passenger', label: t('trips.filter.passenger') },
                ]}
              />
            </div>
            <p className="text-[13px] font-medium text-slate-900 tabular-nums">
              {t('trips.totalKm', { km: formatKm(drivenKm(trips)) })}
            </p>
          </div>
          {sorted.length === 0 ? (
            <EmptyState icon={<Car className="w-12 h-12" strokeWidth={1.75} />} title={t('trips.emptyFilter')} />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>{t('trips.columns.trip')}</TableHeader>
                  <TableHeader className="hidden md:table-cell">{t('trips.columns.route')}</TableHeader>
                  <TableHeader className="hidden sm:table-cell">{t('trips.columns.distance')}</TableHeader>
                  <TableHeader className="hidden md:table-cell">{t('trips.columns.purpose')}</TableHeader>
                  <TableHeader className="hidden lg:table-cell">{t('trips.columns.people')}</TableHeader>
                  <TableHeader>{t('trips.columns.status')}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((trip) => {
                  const open = unparkedOf(trip);
                  const driver = trip.role === DRIVER;
                  const distanceKm = driver ? shownDistanceKm(trip) : null;
                  const route = describeTripRoute(trip);
                  const people = describeTripPeople(trip, t);
                  return (
                    <TableRow key={`${trip.role}-${trip.id}`}>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          <RoleBadge trip={trip} />
                          <span className="text-[13px] font-medium text-slate-900 tabular-nums">
                            {formatDate(parseUtcTimestamp(trip.startedAt), { weekday: 'short', day: '2-digit', month: '2-digit' })}
                          </span>
                        </div>
                        <div className="mt-1 text-[12px] text-slate-600 tabular-nums">{describeTripSpan(trip, t)}</div>
                        {route && <div className="md:hidden text-[12px] text-slate-500">{route}</div>}
                        {people && <div className="lg:hidden text-[12px] text-slate-500">{people}</div>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-[13px] text-slate-700">{route || '–'}</TableCell>
                      <TableCell className="hidden sm:table-cell tabular-nums">
                        {distanceKm != null ? formatKm(distanceKm) : '–'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-[13px] text-slate-700">{describeTripPurpose(trip, t) || '–'}</TableCell>
                      <TableCell className="hidden lg:table-cell text-[13px] text-slate-700">{people || '–'}</TableCell>
                      <TableCell>
                        {open ? (
                          <Button size="sm" variant="secondary" onClick={() => setParking(open)} leftIcon={<ParkingSquare className="w-3.5 h-3.5" strokeWidth={2} />}>
                            {t('vehicles:notParked.action')}
                          </Button>
                        ) : trip.isUnparked ? (
                          <Badge variant="warning">{t('trips.notParked')}</Badge>
                        ) : isTripRunning(trip) ? (
                          <Badge variant="info">{t('trips.running')}</Badge>
                        ) : (
                          <Badge variant="success">{t('trips.ended')}</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      )}
      <ParkVehicleDialog trip={parking} onClose={() => setParking(null)} />
    </Card>
  );
}
